/* eslint-disable react/prop-types */
/* eslint-disable import/no-cycle */

import React, { useState, useCallback, useRef } from 'react';

import Toast from '@ntuctech/devex-tangram/Toast';
import SvgSuccess from '@ntuctech/devex-tangram/Icon/SvgSuccess';

import { Formik } from 'formik';

import { useAuth0 } from '../../Auth0Provider';
import { useFlowControl } from '../../FlowControlProvider';
import { FLOW_STEPS } from '../../../constants';

import PageContent from '../../theme/PageContent';
import PageHeader from '../../theme/PageHeader';
import FormDesc from '../../theme/FormDesc';
import SimpleModal from '../../theme/SimpleModal';
import ErrorBanner from '../../theme/ErrorBanner';
import BackButtonDesktop from '../../theme/BackButtonDesktop';

import VerifyMobileNumberForm from './VerifyPhoneNumberForm';

import Logger from '../../../services/logger';
import Api from '../../../services/api';

const gaScreenName = 'OTP';
const gaEventActionPopup = 'Popup Click';
const gaEventLabelPopup = 'Popup Show';

const ModalReverifyLoginToExistingAccount = ({
  showModalReverifyLoginToExistAcc,
  goToVerifyEmail,
  goToPhoneSignUp,
}) => {
  const gaScreenNameVerifyEmail = 'Verify Email';

  const { trackPwl } = useAuth0();

  React.useEffect(() => {
    if (showModalReverifyLoginToExistAcc) {
      trackPwl({
        screen_name: gaScreenNameVerifyEmail,
        event_action: gaEventLabelPopup,
        event_label: 'SignIn_Mobile_Rever',
      });
    }
  }, [showModalReverifyLoginToExistAcc, trackPwl]);

  const handleGoToVerifyEmail = (...args) => {
    trackPwl({
      screen_name: gaScreenNameVerifyEmail,
      event_action: gaEventActionPopup,
      event_label: 'SignIn_Mobile_Rever_Email',
    });
    goToVerifyEmail(...args);
  };

  const handleGoToPhoneSignUp = (...args) => {
    trackPwl({
      screen_name: gaScreenNameVerifyEmail,
      event_action: gaEventActionPopup,
      event_label: 'SignIn_Mobile_Rever_CreateNew',
    });
    goToPhoneSignUp(...args);
  };

  return (
    <SimpleModal
      open={showModalReverifyLoginToExistAcc}
      title="Log in to an existing account?"
      body={
        <>
          It’s been a while since your last login. Please verify your account via email.
          <br />
          <br />
          If this is not your account, please create a new account.
        </>
      }
      ctaText="Verify email"
      ctaAction={handleGoToVerifyEmail}
      secondaryText="Create an account"
      secondaryAction={handleGoToPhoneSignUp}
      testId="modalReverifyEmail"
    />
  );
};

const ModalExistingAccountMobileNotVerified = ({
  showModalExistingAccountMobileNotVerified,
  clearAndRestart,
  goToPhoneSignUp,
}) => {
  const gaScreenNameVerifyEmail = 'OTP';

  const { trackPwl } = useAuth0();

  React.useEffect(() => {
    if (showModalExistingAccountMobileNotVerified) {
      trackPwl({
        screen_name: gaScreenNameVerifyEmail,
        event_action: gaEventLabelPopup,
        event_label: 'SignIn_Mobile_Email_Exist',
      });
    }
  }, [showModalExistingAccountMobileNotVerified, trackPwl]);

  const handleClearAndRestart = (...args) => {
    trackPwl({
      screen_name: gaScreenNameVerifyEmail,
      event_action: gaEventActionPopup,
      event_label: 'SignIn_Mobile_Email_Exist_Login',
    });
    clearAndRestart(...args);
  };

  const handleGoToPhoneSignUp = (...args) => {
    trackPwl({
      screen_name: gaScreenNameVerifyEmail,
      event_action: gaEventActionPopup,
      event_label: 'SignIn_Mobile_Email_Exist_CreateAccount',
    });
    goToPhoneSignUp(...args);
  };

  return (
    <SimpleModal
      open={showModalExistingAccountMobileNotVerified}
      title="Please enable mobile number login"
      body={
        <>
          <>
            You will need to{' '}
            <strong>log in via email, Google or Apple first, then verify your mobile number</strong>{' '}
            once.
          </>
          <br />
          <br />
          If you don’t have an account, please create an account.
        </>
      }
      ctaText="Back to login"
      ctaAction={handleClearAndRestart}
      secondaryText="Create an account"
      secondaryAction={handleGoToPhoneSignUp}
      testId="modalFoundEmailLinked"
    />
  );
};

const VerifyPhoneNumber = () => {
  const {
    updateUserContext,
    resetUserContext,
    userContext,
    passwordlessStart,
    passwordlessLogin,
    trackPwl,
  } = useAuth0();
  const { goNext, reStart } = useFlowControl();
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const formikBagRef = useRef({});

  const [showModalExistingAccountMobileNotVerified, setShowModalExistingAccountMobileNotVerified] =
    useState(false);
  const [showModalReverifyLoginToExistAcc, setShowModalReverifyLoginToExistAcc] = useState(false);

  const resetFormValues = () => {
    const { resetForm } = formikBagRef.current;
    if (resetForm) {
      resetForm();
    }
  };

  const hideModals = useCallback(() => {
    setShowModalExistingAccountMobileNotVerified(false);
    setShowModalReverifyLoginToExistAcc(false);
  }, [setShowModalReverifyLoginToExistAcc]);

  const resetMessages = useCallback(() => {
    setError(null);
    setSuccessMsg(null);
  }, []);

  const clearAndRestart = useCallback(() => {
    hideModals();
    reStart();
    resetUserContext();
  }, [hideModals, reStart, resetUserContext]);

  const goToVerifyEmail = useCallback(() => {
    hideModals();
    goNext(FLOW_STEPS.VERIFY_EMAIL);
  }, [hideModals, goNext]);

  const goToPhoneSignUp = useCallback(() => {
    hideModals();
    goNext(FLOW_STEPS.PHONE_SIGNUP);
  }, [hideModals, goNext]);

  const sendOtp = async ({ phoneNumber }) => {
    await new Promise((resolve, reject) => {
      passwordlessStart({ phoneNumber }, (err) => {
        if (err) {
          reject(err);
        }
        resolve();
      });
    });
  };

  React.useEffect(() => {
    async function initSendOtp() {
      if (userContext.phoneNumber) {
        sendOtp({ phoneNumber: userContext.phoneNumber }).catch((e) => {
          setError(e);
          Logger.error(e);
        });
      }
    }

    initSendOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContext.phoneNumber]);

  const handleReSendOtp = async () => {
    resetMessages();
    resetFormValues();

    sendOtp({ phoneNumber: userContext.phoneNumber })
      .then(() => {
        setSuccessMsg('One-Time password is sent.');
      })
      .catch((e) => {
        setError(e);
        Logger.error(e);
      });

    return false;
  };

  React.useEffect(
    () => {
      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Landed',
        event_label: 'SignIn_OTP',
      });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onSubmit = async (values, actions) => {
    const { verificationCode } = values;
    formikBagRef.current = actions;

    try {
      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Click',
        event_label: 'SignIn_OTP_Verify',
      });

      const responseData = await Api.getPhoneAvailability({
        phoneNumber: userContext.phoneNumber,
        verificationCode,
      });

      // update otp value
      updateUserContext({ verificationCode });

      const { available, exists, reverify } = responseData;

      // show enable mobile login modal
      if (available === true && exists === true) {
        setShowModalExistingAccountMobileNotVerified(true);
        return;
      }

      // redirect to phone signup
      if (available === true && exists === false) {
        goToPhoneSignUp();
        return;
      }

      // show reverify modal
      if (available === false && reverify === true) {
        setShowModalReverifyLoginToExistAcc(true);
        return;
      }

      // Complete passwordless authentication
      if (available === false && reverify === false) {
        await new Promise((resolve) => {
          passwordlessLogin({ phoneNumber: userContext.phoneNumber, verificationCode }, (err) => {
            if (err) {
              throw err;
            }
            resolve();
          });
        });
        return;
      }
    } catch (err) {
      updateUserContext({ verificationCode: null });
      setError(err);
      Logger.error(err);
    }

    actions.setSubmitting(false);
  };

  const initialValues = { verificationCode: '' };
  const phoneNumberDisplay = userContext.phoneNumber
    ? userContext.phoneNumber.replace(/(\d{4})(\d{4})/, '$1 $2')
    : '';

  return (
    <>
      <BackButtonDesktop action={clearAndRestart} />
      <PageContent>
        <ModalReverifyLoginToExistingAccount
          showModalReverifyLoginToExistAcc={showModalReverifyLoginToExistAcc}
          goToVerifyEmail={goToVerifyEmail}
          goToPhoneSignUp={goToPhoneSignUp}
        />
        <ModalExistingAccountMobileNotVerified
          showModalExistingAccountMobileNotVerified={showModalExistingAccountMobileNotVerified}
          clearAndRestart={clearAndRestart}
          goToPhoneSignUp={goToPhoneSignUp}
        />

        <PageHeader header="Verify your number" backAction={clearAndRestart} />
        <FormDesc>
          Please enter the 6-digit code sent to <strong>{phoneNumberDisplay}</strong>
        </FormDesc>
        {error && <ErrorBanner error={error} onLinkClick={handleReSendOtp} />}
        {successMsg && (
          <Toast inline icon={<SvgSuccess />} message={successMsg} data-testid="successMsg" />
        )}
        <Formik onSubmit={onSubmit} initialValues={initialValues}>
          {(formikProps) => (
            <VerifyMobileNumberForm
              {...formikProps}
              sendOtp={handleReSendOtp}
              formError={error}
              setFormError={setError}
            />
          )}
        </Formik>
      </PageContent>
    </>
  );
};

export default VerifyPhoneNumber;
